import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Trash2, Loader2 } from "lucide-react";
import api from "../lib/api";
import { Button } from "@/components/ui/button";

const SUGGESTIONS = [
  "What should I focus on today?",
  "Explain the sustainability diagnostic",
  "Draft a follow-up email for a stale lead",
  "How do I price a carbon footprint project?",
];

export default function Copilot({ open, onClose, context }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open) {
      api.get("/copilot/history").then(({ data }) => setMessages(data)).catch(() => {});
    }
  }, [open]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/copilot/chat", { message: msg, context: context || "" });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "I couldn't reach the AI service. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const clear = async () => { await api.delete("/copilot/history").catch(() => {}); setMessages([]); };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className="fixed inset-0 bg-slate-900/40 z-40" data-testid="copilot-overlay" />
          <motion.div initial={{ x: 440 }} animate={{ x: 0 }} exit={{ x: 440 }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full sm:w-[420px] bg-slate-900 text-white shadow-2xl z-50 flex flex-col border-l border-slate-800"
            data-testid="copilot-drawer">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <div className="font-display font-semibold text-sm">AI Copilot</div>
                  <div className="text-[10px] text-slate-400 font-mono uppercase tracking-wider">Suggests · you approve</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={clear} data-testid="copilot-clear" className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"><Trash2 className="w-4 h-4" /></button>
                <button onClick={onClose} data-testid="copilot-close" className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {messages.length === 0 && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-400">Ask me anything about your pipeline, a diagnostic, pricing, or drafting. I only suggest — you review and approve.</p>
                  <div className="space-y-2">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} onClick={() => send(s)} data-testid="copilot-suggestion"
                        className="block w-full text-left text-sm px-3 py-2 rounded-lg bg-slate-800/60 hover:bg-slate-800 text-slate-200 transition-colors border border-slate-700/50">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                  <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-emerald-600 text-white rounded-br-sm" : "bg-slate-800 text-slate-100 rounded-bl-sm"}`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Thinking…</div>}
              <div ref={endRef} />
            </div>

            <div className="p-4 border-t border-slate-800">
              <div className="flex items-end gap-2">
                <textarea value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  data-testid="copilot-input" rows={1} placeholder="Ask the Copilot…"
                  className="flex-1 resize-none bg-slate-800 text-white placeholder-slate-500 text-sm rounded-xl px-3.5 py-2.5 outline-none focus:ring-2 focus:ring-emerald-500/50 max-h-32" />
                <Button onClick={() => send()} data-testid="copilot-send" disabled={loading || !input.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 h-10 w-10 p-0 flex-shrink-0 rounded-xl">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
